import { Button, Dialog, DialogContent, DialogTitle, Fade, Tooltip } from '@mui/material';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useStream } from '../../hooks/useStream';
import StateService from '../../services/stateService/StateService';
import useStyles from './styles';
import { Chat, ChartColumn, ChartLine, DataTable, Image, Rss, Sun, SummaryKpi, Template, Time, Video, Webhook } from '@carbon/icons-react';
import { distinctUntilChanged } from 'rxjs';
import { WidgetTypeEnum } from '../../models/Index';
import { useEffect } from 'react';

type Props = {
    addWidgetFunc: (propChartType: string) => void;
    closeEditor: () => void;
};

const WidgetSelection = ({ addWidgetFunc, closeEditor }: Props) => {
    const classes = useStyles();
    const [selectedChart, setSelectedChart] = useStream(StateService.chartType$, distinctUntilChanged());
    const [currentCustomerId, _setCurrentCustomerId] = useStream(StateService.currentCustomerUuid$);
    const [cockpitCustomers, _setCockpitCustomers] = useStream(StateService.cockpitCustomers$);
    const [widgetData, _setWidgetData] = useStream(StateService.widgetData$);
    const basicChartTypes = [
        {
            chartType: WidgetTypeEnum.CLOCK,
            chartName: t('WIDGET_EDITOR.WIDGET_TYPE_CLOCK_DROPDOWN'),
            icon: <Time />,
        },
        {
            chartType: WidgetTypeEnum.FREE_TEXT,
            chartName: t('WIDGET_EDITOR.WIDGET_TYPE_FREE_TEXT_DROPDOWN'),
            icon: <Chat />,
        },
        {
            chartType: WidgetTypeEnum.IFRAME,
            chartName: t('WIDGET_EDITOR.WIDGET_TYPE_IFRAME_DROPDOWN'),
            icon: <Webhook />,
        },
        {
            chartType: WidgetTypeEnum.PICTURE,
            chartName: t('WIDGET_EDITOR.WIDGET_TYPE_PICTURE_DROPDOWN'),
            icon: <Image />,
        },
        {
            chartType: WidgetTypeEnum.RSS_FEED,
            chartName: t('WIDGET_EDITOR.WIDGET_TYPE_RSS_FEED_DROPDOWN'),
            icon: <Rss />,
        },
        {
            chartType: WidgetTypeEnum.VIDEO,
            chartName: t('WIDGET_EDITOR.WIDGET_TYPE_VIDEO_DROPDOWN'),
            icon: <Video />,
        },
        {
            chartType: WidgetTypeEnum.WEATHER,
            chartName: t('WIDGET_EDITOR.WIDGET_TYPE_WEATHER_DROPDOWN'),
            icon: <Sun />,
        },
        {
            chartType: WidgetTypeEnum.SINGLE_VALUE,
            chartName: t('WIDGET_EDITOR.WIDGET_TYPE_SINGLE_VALUE_DROPDOWN'),
            icon: <SummaryKpi />,
        },
    ];
    const dashboardChartType = [
        {
            chartType: WidgetTypeEnum.SUPERSET_DASHBOARD,
            chartName: t('WIDGET_EDITOR.WIDGET_TYPE_SUPERSET_DASHBOARD_LABEL'),
            icon: <Template />,
        },
    ];

    const applyChanges = () => {
        addWidgetFunc(selectedChart);
    };

    const handleSelection = (selectionType) => {
        setSelectedChart(selectionType);
    };

    return (
        <>
            <Dialog
                open={true}
                maxWidth="lg"
                classes={{ paper: classes.dialogWrapper }}
                BackdropProps={{ className: classes.backdrop, invisible: true }}
                data-testid="widget-selection"
            >
                <div className={classes.dialogInnerWrapper}>
                    <DialogTitle>
                        <div className={classes.widgetTitle}>
                            <div>
                                <Typography variant="h4">
                                    <span>{t('WIDGET_EDITOR.WIDGET_SELECTION').toString()}</span>
                                </Typography>
                            </div>
                        </div>
                    </DialogTitle>
                    <DialogContent>
                        <div className={classes.editor}>
                            <div className={classes.basicWidgets}>
                                <div>
                                    <Typography variant="h6">
                                        <span className={classes.settingsLabel}>{t('WIDGET_EDITOR.BASIC_WIDGETS').toString()}</span>
                                    </Typography>
                                </div>
                                <div className={classes.outerWrapper}>
                                    {basicChartTypes.map((chart, index) => (
                                        <Tooltip
                                            classes={{
                                                tooltip: classes.tooltip,
                                            }}
                                            title={chart.chartName}
                                            placement="left-start"
                                            TransitionComponent={Fade}
                                            data-testid="filter-button"
                                            arrow
                                            key={index}
                                        >
                                            <div
                                                key={index}
                                                className={
                                                    selectedChart === chart.chartType
                                                        ? `${classes.currentSelection} ${classes.chartWrapper}`
                                                        : classes.chartWrapper
                                                }
                                                onClick={() => handleSelection(chart.chartType)}
                                            >
                                                {chart.icon}
                                            </div>
                                        </Tooltip>
                                    ))}
                                </div>
                            </div>
                            {widgetData?.length === 0 && (
                                <div className={classes.statistics}>
                                    <>
                                        <Typography variant="h6">
                                            <span className={classes.settingsLabel}>
                                                {t('WIDGET_EDITOR.STATISTICAL_WIDGETS_HEADLINE_LABEL').toString()}
                                            </span>
                                        </Typography>
                                        <div className={classes.outerWrapper}>
                                            {dashboardChartType.map((chart, index) => (
                                                <Tooltip
                                                    classes={{
                                                        tooltip: classes.tooltip,
                                                    }}
                                                    title={chart.chartName}
                                                    placement="left-start"
                                                    TransitionComponent={Fade}
                                                    data-testid="filter-button"
                                                    arrow
                                                    key={index}
                                                >
                                                    <div
                                                        key={index}
                                                        className={
                                                            selectedChart === chart.chartType
                                                                ? `${classes.currentSelection} ${classes.chartWrapper}`
                                                                : classes.chartWrapper
                                                        }
                                                        onClick={() => handleSelection(chart.chartType)}
                                                    >
                                                        {chart.icon}
                                                    </div>
                                                </Tooltip>
                                            ))}
                                        </div>
                                    </>
                                </div>
                            )}
                        </div>
                    </DialogContent>
                    <div className={classes.buttonWrapper}>
                        <Button variant="outlined" color="primary" onClick={closeEditor} data-testid="close" size="large">
                            {t('COMMON.CANCEL_BUTTON').toString()}
                        </Button>
                        <Button variant="contained" color="primary" onClick={applyChanges} data-testid="done" size="large">
                            {t('COMMON.CONFIRM_BUTTON').toString()}
                        </Button>
                    </div>
                </div>
            </Dialog>
        </>
    );
};

export default WidgetSelection;
